import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Button from "../Button";
import SplitButton from "../SplitButton";
import Tooltip from "../../tooltip/Tooltip";
import { formatText } from "../../../_utils/textFormatter";
import "./_page-header-bar.scss";

const normalizeSize = (size) => {
  if (size === "small") return "sm";
  if (size === "medium") return "md";
  if (size === "large") return "lg";
  return size || "sm";
};

/**
 * A single action button config: { btnText, variant, size, onClick, disabled,
 * loading, className, title }. `title` renders as a hover tooltip (not a
 * native HTML title attribute) - matches the old ContentHeader's behavior.
 * `altText`/`emoji`/`fit` from the old reusables-Button-era configs are
 * accepted but ignored - the reusables Button never read them either.
 */
const HeaderButton = ({ button }) => {
  const {
    btnText,
    altText,
    emoji,
    title,
    size,
    fit,
    variant,
    menuItems,
    ...rest
  } = button;
  const btn = menuItems ? (
    <SplitButton size={normalizeSize(size)} items={menuItems} {...rest}>
      {btnText}
    </SplitButton>
  ) : (
    <Button variant={variant} size={normalizeSize(size)} {...rest}>
      {btnText}
    </Button>
  );

  return (
    <div>
      {title ? (
        <Tooltip content={title} position="top">
          {btn}
        </Tooltip>
      ) : (
        btn
      )}
    </div>
  );
};

/**
 * `breadcrumb`: optional array of `{ label, to? }` rendered above the title
 * row, `ChevronRight`-separated - a detail page's "Companies / Acme Corp"
 * trail. The last entry (the current record) typically has no `to` and
 * renders as plain text rather than a `Link`. Kept as data instead of a
 * node so every consumer gets identical markup/spacing instead of each
 * detail page re-deriving its own breadcrumb JSX (which is how
 * CompanyDetails and SubscriptionDetails ended up as near-duplicate files).
 *
 * `breadcrumbAdornment`: optional node rendered at the end of the
 * breadcrumb row itself, same line - e.g. a `StatusTag` right after
 * "Companies / Acme Corp". For a breadcrumb-only header (no separate
 * `title`/`titleAdornment` row below it - see CompanyDetails/
 * SubscriptionDetails, the only current consumers), this is where the
 * status pill goes instead.
 *
 * `titleAdornment`: optional node rendered next to the title, for e.g. a
 * `StatusTag`. Deliberately separate from `titleBadge` (below), which wraps
 * its child in the mono glass-pill chrome built for a record-count badge -
 * wrong chrome for a status indicator. Only meaningful when `title` is
 * also passed - the title row itself doesn't render otherwise (see below).
 */
const PageHeaderBar = ({
  title,
  titleBadge,
  titleAdornment,
  breadcrumb,
  breadcrumbAdornment,
  desc,
  buttons = [],
  headerImg,
  className,
  descComponent,
  disableButton = true,
  HeaderSvg,
  rightComponent,
  centerComponent,
  searchComponent,
  belowComponent,
  HeaderIcon, // optional explicit lucide icon component
}) => {
  return (
    <header
      className={`page-header-bar ${belowComponent ? "page-header-bar--stacked" : ""} ${
        className ? className : ""
      }`}
    >
      <div
        className={`page-header-bar__row ${searchComponent ? "page-header-bar__row--with-search" : ""}`}
      >
        <div className="page-header-bar__left">
          {/* {PathIcon ? (
            <PathIcon className="page-header-bar__icon" size={20} strokeWidth={1.5} />
          ) : HeaderSvg ? (
            <HeaderSvg className="page-header-bar__icon" />
          ) : headerImg ? (
            <img className="page-header-bar__icon" src={headerImg} alt="" />
          ) : null} */}
          <div className="page-header-bar__text">
            {breadcrumb?.length > 0 && (
              <div className="page-header-bar__breadcrumb">
                {breadcrumb.map((crumb, idx) => (
                  <span className="page-header-bar__breadcrumb-item" key={crumb.label ?? idx}>
                    {idx > 0 && (
                      <ChevronRight
                        size={13}
                        className="page-header-bar__breadcrumb-sep"
                        aria-hidden="true"
                      />
                    )}
                    {crumb.to ? (
                      <Link to={crumb.to} className="page-header-bar__breadcrumb-link">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="page-header-bar__breadcrumb-current">{crumb.label}</span>
                    )}
                  </span>
                ))}
                {breadcrumbAdornment && (
                  <span className="page-header-bar__breadcrumb-adornment">{breadcrumbAdornment}</span>
                )}
              </div>
            )}
            {(title || titleAdornment || titleBadge) && (
              <div className="page-header-bar__title-row">
                {title && <h2 className="page-header-bar__title">{title}</h2>}
                {titleAdornment}
                {titleBadge && (
                  <span className="page-header-bar__title-badge">
                    {titleBadge}
                  </span>
                )}
              </div>
            )}
            {desc && (
              <p className="page-header-bar__desc">{formatText(desc)}</p>
            )}
            {descComponent && descComponent}
          </div>
        </div>
        {centerComponent && (
          <div className="page-header-bar__center">{centerComponent}</div>
        )}
        <div className="page-header-bar__actions">
          {searchComponent && (
            <div className="page-header-bar__search">{searchComponent}</div>
          )}
          {rightComponent && rightComponent}
          {disableButton &&
            buttons.map((button, idx) => (
              <HeaderButton key={idx} button={button} />
            ))}
        </div>
      </div>
      {/* Design keeps stat cards + tabs on the same glass panel as the
          title row (one continuous header block with a single bottom
          border), not as separate sections sitting on the page background
          below it - this slot renders them inside that same <header>. */}
      {belowComponent && (
        <div className="page-header-bar__below">{belowComponent}</div>
      )}
    </header>
  );
};

export default PageHeaderBar;
